/**
 * FeeBreakdownDialog — line-item breakdown of a fee before payment.
 *
 * Backed by GET /api/parent/fees/{fee_id}/breakdown. Renders:
 *   - Per-component rows (Admission / Tuition / Hostel / etc.)
 *   - Optional scholarship / employee-child discount line
 *   - Adjustment line when the configured total differs from the stored amount
 *   - Final payable + a "Pay now" button that delegates to the parent's payFee.
 */
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { ListBullets, CreditCard } from "@phosphor-icons/react";

const fmt = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency", currency: "INR", maximumFractionDigits: 0,
  }).format(Number(n) || 0);

export function FeeBreakdownButton({ fee, onPay }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setData(null);
    setErr("");
    api.get(`/parent/fees/${fee.id}/breakdown`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, [open, fee.id]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`parent-fee-breakdown-${fee.id}`}>
          <ListBullets size={12} className="mr-1.5" /> Breakdown
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Fee breakdown · {fee.period}</DialogTitle>
        </DialogHeader>
        {err && <div className="text-sm text-[var(--vs-danger)]">{err}</div>}
        {!data && !err && (
          <div className="text-sm text-[var(--vs-text-secondary)]">Loading breakdown…</div>
        )}
        {data && (
          <div className="space-y-3" data-testid={`fee-breakdown-content-${fee.id}`}>
            <div className="text-xs text-[var(--vs-text-secondary)]">
              For <strong>{data.student_name}</strong> · Class {data.class_name || "—"}
            </div>
            <table className="w-full text-sm">
              <tbody>
                {data.items.map((it) => (
                  <tr key={it.component + it.label} className="border-b border-[var(--vs-border)]/40">
                    <td className="py-2">{it.label}</td>
                    <td className="py-2 text-right font-mono">{fmt(it.amount)}</td>
                  </tr>
                ))}
                <tr className="border-b border-[var(--vs-border)]/40 text-[var(--vs-text-secondary)]">
                  <td className="py-2 text-xs">Subtotal</td>
                  <td className="py-2 text-xs text-right font-mono">{fmt(data.subtotal)}</td>
                </tr>
                {data.discount_pct > 0 && (
                  <tr className="border-b border-[var(--vs-border)]/40 text-[var(--vs-success)]" data-testid="fee-breakdown-discount">
                    <td className="py-2">
                      {data.discount_reason || "Discount"}
                      <Badge variant="outline" className="ml-2 text-[10px] border-[var(--vs-success)]/40 text-[var(--vs-success)]">
                        −{data.discount_pct}%
                      </Badge>
                    </td>
                    <td className="py-2 text-right font-mono">−{fmt(data.discount_amount)}</td>
                  </tr>
                )}
                {data.sibling_discount_pct > 0 && (
                  <tr className="border-b border-[var(--vs-border)]/40 text-[var(--vs-success)]" data-testid="fee-breakdown-sibling">
                    <td className="py-2">
                      {data.sibling_discount_reason || "Sibling discount"}
                      <Badge variant="outline" className="ml-2 text-[10px] border-[var(--vs-success)]/40 text-[var(--vs-success)]">
                        −{data.sibling_discount_pct}%
                      </Badge>
                    </td>
                    <td className="py-2 text-right font-mono">−{fmt(data.sibling_discount_amount)}</td>
                  </tr>
                )}
                {Math.abs(data.adjustment) >= 0.5 && (
                  <tr className="border-b border-[var(--vs-border)]/40 text-[var(--vs-text-secondary)]" data-testid="fee-breakdown-adjustment">
                    <td className="py-2 text-xs italic">
                      Manual adjustment <span className="text-[10px]">(set by admin)</span>
                    </td>
                    <td className="py-2 text-xs text-right font-mono">
                      {data.adjustment > 0 ? "+" : ""}{fmt(data.adjustment)}
                    </td>
                  </tr>
                )}
                <tr className="font-display text-base font-semibold">
                  <td className="pt-3">Amount payable</td>
                  <td className="pt-3 text-right font-mono text-[var(--vs-secondary)]" data-testid="fee-breakdown-total">
                    {fmt(data.amount_payable)}
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="text-[11px] text-[var(--vs-text-secondary)]">
              Pay securely with card, UPI, NetBanking, or Stripe wallet. A 80G-style receipt PDF is emailed instantly.
            </div>
            {fee.status !== "paid" && fee.status !== "sponsored" && (
              <Button
                onClick={() => { setOpen(false); onPay?.(fee.id); }}
                data-testid={`fee-breakdown-pay-${fee.id}`}
                className="w-full bg-[var(--vs-secondary)] text-[var(--vs-bg)]"
              >
                <CreditCard size={14} className="mr-1.5" /> Pay {fmt(data.amount_payable)} now
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default FeeBreakdownButton;
